import React, { useEffect, useState } from "react";
import { IonIcon, IonInput, IonItem, IonLabel, IonSpinner } from "@ionic/react";
import { useForm, Controller } from "react-hook-form";
import { Address } from "../../../../../core/types/entities";
import {
  useCreateCustomerAddress,
  useGetCustomerAddresses,
  useUpdateCustomerAddress,
} from "../../../../../providers/CustomerAddresses";
import { useCustomerAddressesStorageData } from "../../../../../providers/customerAddressesStorage";
import { useAuth } from "../../../../../contexts/auth-context";
import { useGetShippingZonesQuery } from "../../../../../providers/shippingAndPayment";

import { addressSelectionModalInfo } from "./constants";
import { addOutline } from "ionicons/icons";
import { useCustomerAddressesStateData } from "../../../../../providers/CustomerAddresses/CustomerAddressesStateContext";
import { getFullAddressForAddressType } from "../../../../../utils";
import { useTranslation } from "react-i18next";
import { useExpressDeliveryCheck } from "../../../../../providers/address";
import { toast } from "react-toastify";
import { AddressWithoutFlatType } from "../types";
import { CityCombobox } from "./CityCombobox";
import { useConfig } from "../../../../../providers/config";
import { shouldUseStaticOrderData } from "../../../../../utils/shouldUseStaticOrderData";

type CreateAddressFormProps = {
  goBack: () => void;
  displayAddresses: Address[];
};

const CreateAddressForm = ({ goBack, displayAddresses }: CreateAddressFormProps) => {
  const { t } = useTranslation();
  const config = useConfig();
  const { user } = useAuth();
  const customerId = user?.id;
  const {
    data: fetchedCities = [],
    isLoading: isShippingZonesLoading,
    isError: isShippingZonesError,
  } = useGetShippingZonesQuery();
  const { customerAddress, setCustomerAddress } = useCustomerAddressesStateData();
  const { mutate: updateAddressMutate, isPending: isUpdateAddressLoading } = useUpdateCustomerAddress();
  const { checkAndSetExpressMode } = useExpressDeliveryCheck();
  const [focusedInput, setFocusedInput] = useState<string | null>(null);

  const { isUserAuthenticated } = useAuth();
  const { mutate: createAddressMutate, isPending: isCreateAddressLoading } = useCreateCustomerAddress();
  const { data: customerAddresses } = useGetCustomerAddresses({ customerId });
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { isValid },
  } = useForm<AddressWithoutFlatType>({
    mode: "onSubmit",
    defaultValues: {
      city: customerAddress?.city ?? "",
      street: customerAddress?.street ?? "",
      house: customerAddress?.house ?? "",
      entrance: customerAddress?.entrance ?? "",
      floor: customerAddress?.floor ?? "",
      intercom: customerAddress?.intercom ?? "",
      latitude: customerAddress?.latitude ?? "",
      longitude: customerAddress?.longitude ?? "",
      apartmentNumber: customerAddress?.apartmentNumber ?? "",
    },
  });

  const shouldUseStaticCities = shouldUseStaticOrderData({
    staticOrderDataEnabled: config.staticOrderDataEnabled,
    staticOrderData: config.staticOrderData,
    isLoading: isShippingZonesLoading,
    isError: isShippingZonesError,
  });

  const cities = shouldUseStaticCities ? config.staticOrderData!.shippingZones : fetchedCities;

  useEffect(() => {
    const checkedValidAddress =
      customerAddress?.city && cities.length && !cities.find((city) => city.name === customerAddress?.city);
    if (checkedValidAddress) {
      setValue("city", "");
    }
  }, [cities, customerAddress?.city]);

  const { unAuthedCustomerAddresses, addAddress, setCurrentShippingAddressId } = useCustomerAddressesStorageData();

  const filteredCities =
    watch("city") !== undefined && watch("city") !== ""
      ? cities.filter((city) => {
          return city.name.toLowerCase().replace(/\s+/g, "").includes(watch("city")!.toLowerCase().replace(/\s+/g, ""));
        })
      : cities;

  const cityFieldDisabled = cities.length < 1;

  const handleCreateAddressMutate = async (data: Address) => {
    const fullAddressFromForm = getFullAddressForAddressType(data).toLowerCase();
    const fullAddressFromCustomerAddress =
      customerAddress && getFullAddressForAddressType(customerAddress).toLowerCase();

    const isAddressExist = displayAddresses?.find((address) => address.id === customerAddress?.id);

    const isAddressEdited = customerAddress && fullAddressFromForm !== fullAddressFromCustomerAddress;

    const isDuplicate = displayAddresses?.some(
      (address) => getFullAddressForAddressType(address).toLowerCase() === fullAddressFromForm,
    );

    if (isDuplicate) {
      toast.warn(t("Such address already exists!"));
      return;
    }
    console.log({ isUserAuthenticated });

    if (isUserAuthenticated) {
      const handleOnSuccess = () => {
        setCustomerAddress(undefined);
        reset({
          city: "",
          street: "",
          house: "",
          entrance: "",
          floor: "",
          intercom: "",
          latitude: "",
          longitude: "",
        });
        goBack();
      };

      console.log({ ok: isAddressExist && isAddressEdited });

      if (isAddressExist && isAddressEdited) {
        updateAddressMutate(
          { address: { ...data, id: isAddressExist.id }, customerId: customerId! },
          {
            onSuccess: (response: Address | null) => {
              if (response?.id) {
                setCurrentShippingAddressId(response?.id, "CurrentShippingAddressSelect");
              }
              handleOnSuccess();
            },
          },
        );
      } else {
        createAddressMutate(
          { customerAddressInput: data, customerId: customerId! },
          {
            onSuccess: async (response) => {
              if (response?.id) {
                setCurrentShippingAddressId(response?.id, "CurrentShippingAddressSelect");
                if (config.isExpressDeliveryEnabled && response?.latitude && response?.longitude) {
                  await checkAndSetExpressMode({
                    latitude: response.latitude,
                    longitude: response.longitude,
                  });
                }
              }
              handleOnSuccess();
            },
          },
        );
      }
    } else {
      const unAuthedCustomerAddressesId = (unAuthedCustomerAddresses.length + 1).toString();
      addAddress({ ...data, id: unAuthedCustomerAddressesId });
      console.log({ unAuthedCustomerAddressesId });
      console.log({ data });
      setCustomerAddress(undefined);
      setCurrentShippingAddressId(unAuthedCustomerAddressesId);
      if (config.isExpressDeliveryEnabled && data?.latitude && data?.longitude) {
        await checkAndSetExpressMode({
          latitude: data.latitude,
          longitude: data.longitude,
        });
      }
      goBack();
    }
  };

  return (
    <form onSubmit={handleSubmit(handleCreateAddressMutate)} className="flex flex-col justify-between h-full">
      <div className="flex flex-col w-full">
        <Controller
          key="city"
          name="city"
          control={control}
          rules={{
            required: true,
            validate: (cityName) => cities.some((city) => city.name === cityName),
          }}
          render={({ field: { onChange, value }, fieldState: { invalid } }) => (
            <CityCombobox
              label="Город"
              value={value!}
              invalid={invalid}
              onChange={onChange}
              disabled={cityFieldDisabled}
              filteredCities={filteredCities}
            />
          )}
        />
        {addressSelectionModalInfo.map((item) => (
          <Controller
            key={item.name}
            name={item.name as keyof AddressWithoutFlatType}
            control={control}
            rules={{
              required: item.required && t("Required field"),
            }}
            render={({ field: { onChange, value } }) => {
              return (
                <IonItem className="flex items-center w-full" lines="none">
                  <IonLabel position="floating" className="w-full">
                    <div className="flex items-center gap-0.5 w-full">
                      {!value && focusedInput !== item.name && (
                        <IonIcon icon={addOutline} className="text-center text-[1.3rem]" />
                      )}
                      <div>{item.label}</div>
                    </div>
                  </IonLabel>
                  <IonInput
                    value={value}
                    onIonInput={onChange}
                    title={item.label}
                    placeholder={item.placeholder}
                    required={item.required}
                    className="w-full"
                    onIonFocus={() => setFocusedInput(item.name)}
                    onIonBlur={() => setFocusedInput(null)}
                  />
                </IonItem>
              );
            }}
          />
        ))}
      </div>
      <div className="flex justify-evenly items-center w-full py-6">
        <button
          type="submit"
          disabled={isCreateAddressLoading || isUpdateAddressLoading || !isValid}
          className="h-10 flex disabled:opacity-40 items-center justify-center w-2/3 bg-primary text-sm rounded-full"
        >
          {t("Save")}
          {(isCreateAddressLoading || isUpdateAddressLoading) && <IonSpinner className="ml-2 size-5 text-white" />}
        </button>
      </div>
    </form>
  );
};

export default CreateAddressForm;
